<template>
  <!-- Main Content -->
  <main class="py-4 sm:py-8 sm:p-8">
    <!-- Header -->
    <div class="mb-10 flex items-center justify-between">
      <div>
        <PageTitle>
          Disparo de mensagens
        </PageTitle>
        <PageDescription>
          Envie mensagens automatizadas e personalizadas para impulsionar suas vendas.
        </PageDescription>
      </div>
      <RegisterButton @click="openNewMessageModal">Disparar mensagem</RegisterButton>
    </div>

    <!-- Messages Table Card -->
    <TableContainer>
      <!-- Table Header -->
      <div class="mb-6 flex items-center justify-between">
        <h2 class="text-xl font-semibold text-white">Mensagens disparadas</h2>
      </div>

      <!-- Table -->
      <Table class="hidden md:block">
        <TableHeader>
          <TableRow>
            <TableHead
              v-for="header in tableHeaders"
              :key="header.key"
              :class="header.class"
            >
              <div class="flex items-center gap-2">
                {{ header.label }}
                <ArrowsUpDownIcon 
                  v-if="header.sortable"
                  class="h-4 w-4 cursor-pointer text-gray-500 hover:text-white"
                  @click="sortBy(header.key)"
                />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <template v-if="messages">
            <TableFallback v-if="messages.data.length === 0" :colspan="tableHeaders.length" :icon="MessageSquareMore" title="Nenhuma mensagem encontrada" :description="`Clique em 'Disparar mensagem' para começar`" />
            <TableRow
              v-for="message in messages.data"
              :key="message.id"
            >
              <TableCell>
                <div>
                  <div class="text-sm font-medium text-white">{{ new Date(message.sendAt).toLocaleString() }}</div>
                </div>
              </TableCell>
              <TableCell>
                <div>
                  <div class="text-sm font-medium text-white">{{ message.description }}</div>
                </div>
              </TableCell>
              <TableCell>
                <div>
                  <div class="text-sm font-medium text-white">
                    <MessageRecipientTypeBadge :recipient="message.recipientType" />
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div class="flex flex-wrap gap-2">
                  <Badge 
                    variant="default"
                  >
                    {{ message.successCount }}
                  </Badge>
                </div>
              </TableCell>
              <TableCell>
                <div class="flex flex-wrap gap-2">
                  <Badge 
                    variant="destructive"
                  >
                    {{ message.failureCount }}
                  </Badge>
                </div>
              </TableCell>
              <TableCell>
                <div class="flex gap-2">
                  <Switch
                    v-if="message.sendingInterval || message.recipientType === MessageRecipientTypeEnum.CHAT_JOIN_REQUEST_APPROVAL"
                    :checked="message.status === MessageStatusEnum.ACTIVE"
                    @update:checked="(status) => {
                      message.status = status ? MessageStatusEnum.ACTIVE : MessageStatusEnum.INACTIVE
                      update(message.id, { status: status ? MessageStatusEnum.ACTIVE : MessageStatusEnum.INACTIVE }).then(() => {
                        toast({
                          title: 'Status da mensagem atualizado',
                          class: 'bg-green-500 text-white',
                          duration: 1500
                        })
                      })
                    }"
                  />
                  <span class="text-sm" v-else-if="message.successCount > 0 || message.failureCount > 0">Disparado</span>
                  <span class="text-sm" v-else>Pendente</span>
                </div>
              </TableCell>
              <TableCell>
                <Button
                  v-if="message.sendingInterval || message.recipientType === MessageRecipientTypeEnum.CHAT_JOIN_REQUEST_APPROVAL"
                  variant="ghost"
                  size="icon"
                  @click="editMessage(message)"
                >
                  <PencilIcon class="h-4 w-4" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    class=""
                    @click="showMessage(message.id)"
                  >
                  <Eye class="h-4 w-4" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    class="hover:text-red-400"
                    @click="deleteMessage(message)"
                  >
                  <TrashIcon class="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          </template>
          <TableRowSkeleton :columns="tableHeaders.length" v-else />
        </TableBody>
      </Table>

      <!-- Mobile List -->
      <div class="space-y-4 md:hidden text-left" v-if="messages">
        <template v-if="messages.data.length > 0">
          <MobileCard v-for="message in messages.data" :key="message.id">
            <template #header>
               <div class="flex items-center gap-2">
                 <div class="flex flex-col">
                  <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">Disparo</span>
                  <div class="flex items-center gap-2">
                    <span class="text-white font-bold text-lg">{{ new Date(message.sendAt).toLocaleDateString() }}</span>
                    <span class="text-zinc-400 text-xs">{{ new Date(message.sendAt).toLocaleTimeString() }}</span>
                  </div>
                 </div>
               </div>
            </template>
            
            <template #actions>
               <div class="flex flex-col items-end gap-2">
                  <div class="flex items-center gap-1">
                    <Button
                      v-if="message.sendingInterval || message.recipientType === MessageRecipientTypeEnum.CHAT_JOIN_REQUEST_APPROVAL"
                      variant="ghost"
                      size="icon"
                      class="h-8 w-8 text-zinc-400 hover:text-white"
                      @click="editMessage(message)"
                    >
                      <PencilIcon class="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        class="h-8 w-8 text-zinc-400 hover:text-white"
                        @click="showMessage(message.id)"
                      >
                      <Eye class="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        class="h-8 w-8 text-zinc-400 hover:text-red-400"
                        @click="deleteMessage(message)"
                      >
                      <TrashIcon class="h-4 w-4" />
                    </Button>
                  </div>
               </div>
            </template>
            
            <template #content>
               <MobileDataLabel label="Descrição">
                  {{ message.description }}
               </MobileDataLabel>
               
               <MobileDataLabel label="Destinatário">
                   <MessageRecipientTypeBadge :recipient="message.recipientType" />
               </MobileDataLabel>

               <div class="grid grid-cols-2 gap-4 mt-2">
                  <MobileDataLabel label="Sucesso">
                    <Badge variant="default">{{ message.successCount }}</Badge>
                  </MobileDataLabel>
                  <MobileDataLabel label="Falha">
                    <Badge variant="destructive">{{ message.failureCount }}</Badge>
                  </MobileDataLabel>
               </div>

               <div class="flex items-center justify-between pt-4 mt-2 border-t border-white/5">
                  <div class="flex flex-col">
                    <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1">Status</span>
                    <div class="flex gap-2">
                      <Switch
                        v-if="message.sendingInterval || message.recipientType === MessageRecipientTypeEnum.CHAT_JOIN_REQUEST_APPROVAL"
                        :checked="message.status === MessageStatusEnum.ACTIVE"
                        @update:checked="(status) => {
                          message.status = status ? MessageStatusEnum.ACTIVE : MessageStatusEnum.INACTIVE
                          update(message.id, { status: status ? MessageStatusEnum.ACTIVE : MessageStatusEnum.INACTIVE }).then(() => {
                            toast({
                              title: 'Status da mensagem atualizado',
                              class: 'bg-green-500 text-white',
                              duration: 1500
                            })
                          })
                        }"
                      />
                      <span class="text-sm text-white" v-else-if="message.successCount > 0 || message.failureCount > 0">Disparado</span>
                      <span class="text-sm text-white" v-else>Pendente</span>
                    </div>
                  </div>
               </div>
            </template>
          </MobileCard>
        </template>
        <div v-else class="text-center py-12 text-zinc-500">
           <MessageSquareMore class="size-8 mx-auto mb-3 text-zinc-700" />
           Nenhuma mensagem encontrada
        </div>
      </div>

      <!-- Pagination -->
      <Pagination @update:page="(page: string) => reloadAndGetAll({ page })" v-model:per-page="perPage" :data="messages?.pagination" />
    </TableContainer>
  </main>

  <!-- Choice Modal -->
  <Dialog v-model:open="isChoiceModalOpen">
    <DialogScrollContent class="sm:max-w-xl">
      <DialogHeader>
        <div class="flex items-center gap-2">
          <DialogTitle class="text-white">Escolha o tipo de disparo</DialogTitle>
          <TooltipProvider>
            <Tooltip :delay-duration="100">
              <TooltipTrigger as-child>
                <Info class="size-4 text-zinc-500 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" :side-offset="5" class="max-w-xs bg-zinc-900 border-white/10 text-white">
                <p>O tipo de disparo define como a comunicação será iniciada. Fluxos permitem automações complexas, enquanto o padrão é ideal para avisos rápidos.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <DialogDescription class="text-zinc-400">
          Selecione o formato ideal para sua campanha.
        </DialogDescription>
      </DialogHeader>
      
      <div class="grid grid-cols-2 gap-4 py-6">
        <button 
          @click="selectMessageType('standard')"
          class="flex flex-col items-center gap-4 p-8 rounded-2xl border border-white/5 bg-white/5 border-white/10 hover:border-primary/40 hover:bg-primary/[0.04] transition-all duration-300 text-center group relative overflow-hidden"
        >
          <div class="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div class="p-4 rounded-xl bg-white/5 group-hover:bg-primary/10 transition-colors relative z-10">
            <MessageSquareText class="size-8 text-primary" />
          </div>
          <div class="relative z-10">
            <h3 class="font-bold text-white text-lg">Disparo Padrão</h3>
            <p class="text-xs text-zinc-500 mt-2 leading-relaxed">Mensagem direta com textos, mídias e botões de ação.</p>
          </div>
        </button>
        
        <button 
          @click="selectMessageType('flow')"
          class="flex flex-col items-center gap-4 p-8 rounded-2xl border border-white/5 bg-white/5 border-white/10 hover:border-primary/40 hover:bg-primary/[0.04] transition-all duration-300 text-center group relative overflow-hidden"
        >
          <div class="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div class="p-4 rounded-xl bg-white/5 group-hover:bg-primary/10 transition-colors relative z-10">
            <GitMerge class="size-8 text-primary" />
          </div>
          <div class="relative z-10">
            <h3 class="font-bold text-white text-lg">Disparo com Fluxo</h3>
            <p class="text-xs text-zinc-500 mt-2 leading-relaxed">Acione uma automação do Chat Flow pré-configurada.</p>
          </div>
        </button>
      </div>
    </DialogScrollContent>
  </Dialog>

  <DeleteConfirmationDialog v-model:open="isDeleteMessageDialogOpen" :loading="isDeletingMessage" @confirm="isDeletingMessage = true; destroy(deletingMessage!).then(() => {
    toast({
      title: 'Mensagem excluÃ­da com sucesso',
      class: 'bg-green-500 text-white',
      duration: 1500
    })
    isDeletingMessage = false
    isDeleteMessageDialogOpen = false
    const index = messages?.data.findIndex(message => message.id == deletingMessage)
    if (index !== -1) messages?.data.splice(index!, 1)
  })" />

  <!-- New/Edit Message Modal -->
  <Dialog v-model:open="isMessageModalOpen">
    <DialogScrollContent class="max-w-4xl flex flex-col p-0 gap-0 bg-zinc-950 border-zinc-800">
      <DialogHeader class="px-8 py-6 border-b border-zinc-800 bg-zinc-900/50">
        <div class="flex items-center gap-3">
          <div class="p-2.5 rounded-xl bg-primary/10 border border-primary/20">
            <Rocket v-if="!editingMessage" class="size-6 text-primary" />
            <PencilIcon v-else class="size-6 text-primary" />
          </div>
          <div>
            <DialogTitle class="text-2xl font-bold text-white tracking-tight">
              {{ editingMessage ? 'Refinar Disparo' : 'Configurar Novo Disparo' }}
            </DialogTitle>
            <DialogDescription class="text-zinc-400 font-medium">
              {{ editingMessage ? 'Ajuste os detalhes para otimizar sua entrega.' : 'Siga os 3 passos para criar um disparo de alta conversÃ£o.' }}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <div class="flex-1 overflow-hidden flex flex-col items-center">
        <!-- Modern Stepper Navigation -->
        <div class="w-full px-12 py-8 bg-zinc-900/20 border-b border-zinc-800">
           <Stepper v-model="currentStep" class="flex w-full items-start gap-4">
            <StepperItem
              v-for="step in steps"
              :key="step.step"
              class="relative flex w-full flex-col items-center justify-center group"
              :step="step.step"
              :disabled="isStepDisabled(step.step)"
            >
              <StepperTrigger class="flex flex-col items-center gap-3 p-2 rounded-xl transition-all duration-300 hover:bg-white/5">
                <StepperIndicator
                  class="rounded-full w-12 h-12 flex items-center justify-center border-2 transition-all duration-500"
                  :class="[
                    step.step === currentStep 
                      ? 'border-primary bg-primary/20 text-primary shadow-[0_0_20px_rgba(16,185,129,0.2)]' 
                      : step.step < currentStep 
                        ? 'border-primary bg-primary text-black' 
                        : 'border-zinc-700 bg-zinc-900 text-zinc-500'
                  ]"
                >
                  <Check v-if="step.step < currentStep" class="size-6 stroke-[3px]" />
                  <component v-else :is="step.icon" class="size-5" />
                </StepperIndicator>
                <div class="flex flex-col items-center text-center gap-1">
                  <StepperTitle
                    class="text-sm font-bold tracking-wide uppercase transition-colors duration-300"
                    :class="[step.step === currentStep ? 'text-white' : 'text-zinc-500']"
                  >
                    {{ step.title }}
                  </StepperTitle>
                  <StepperDescription
                    class="text-[11px] font-medium transition-colors duration-300 hidden md:block"
                    :class="[step.step === currentStep ? 'text-zinc-400' : 'text-zinc-600']"
                  >
                    {{ step.description }}
                  </StepperDescription>
                </div>
              </StepperTrigger>
              <StepperSeparator
                v-if="step.step !== steps[steps.length - 1].step"
                class="absolute left-[calc(50%+3rem)] right-[calc(-50%+3rem)] top-8 h-0.5 bg-zinc-800 group-data-[state=completed]:bg-primary transition-colors duration-500 hidden sm:block"
              />
            </StepperItem>
          </Stepper>
        </div>

        <!-- Step Content Area -->
        <div class="flex-1 w-full overflow-y-auto custom-scrollbar">
          <div class="max-w-5xl mx-auto px-8 py-10 h-full">
            
            <!-- STEP 1: CONTEÃšDO -->
            <div v-if="currentStep === 1" class="animate-in fade-in slide-in-from-right-4 duration-500">
              <div class="grid lg:grid-cols-[1fr,360px] gap-12">
                <div class="space-y-8">
                  <div class="space-y-3">
                    <div class="flex items-center gap-2">
                       <Label class="text-sm font-bold text-zinc-200 uppercase tracking-widest">Identificação da Campanha <span class="text-red-500">*</span></Label>
                       <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger><HelpCircle class="size-3.5 text-zinc-600" /></TooltipTrigger>
                          <TooltipContent>Nome usado apenas para sua organização interna.</TooltipContent>
                        </Tooltip>
                       </TooltipProvider>
                    </div>
                    <Input 
                      v-model="messageForm.description" 
                      placeholder="Ex: Oferta Relâmpago - Leads VIP" 
                      class="h-12 bg-zinc-900/50 border-zinc-700 focus:border-primary focus:ring-primary/20 transition-all text-sm"
                    />
                  </div>

                  <div v-if="creationMode === 'flow'" class="space-y-4 p-6 rounded-2xl bg-primary/5 border border-primary/10">
                    <div class="flex items-center gap-3 mb-2">
                      <GitMerge class="size-5 text-primary" />
                      <h4 class="font-bold text-white">Automação de Fluxo</h4>
                    </div>
                    <p class="text-sm text-zinc-400 leading-relaxed">Selecione qual jornada o cliente iniciará ao receber esta mensagem.</p>
                    <Select :model-value="(messageForm.flowId as any)" @update:model-value="v => messageForm.flowId = v">
                      <SelectTrigger class="h-12 bg-zinc-950 border-zinc-700 text-white">
                        <SelectValue placeholder="Escolha um fluxo existente..." />
                      </SelectTrigger>
                      <SelectContent class="bg-zinc-900 border-zinc-700">
                        <SelectItem v-for="f in flowsOptions" :key="f.id" :value="String(f.id)" class="text-zinc-300 focus:bg-primary focus:text-black">
                          {{ f.name }}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <template v-if="creationMode !== 'flow'">
                    <div class="space-y-6">
                      <div class="space-y-3">
                        <Label class="text-sm font-bold text-zinc-200 uppercase tracking-widest">Visual & Mídia</Label>
                        <AnyFileUpload 
                          class="w-full" 
                          v-model:preview="startMediaPreview" 
                          v-model="messageForm.startMedia" 
                          v-model:has-spoiler="messageForm.hasSpoiler"
                          label="Arraste uma imagem ou vídeo marcante" 
                        />
                      </div>

                      <div class="space-y-3">
                        <Label class="text-sm font-bold text-zinc-200 uppercase tracking-widest">Mensagem de Texto</Label>
                        <RichTextarea 
                          v-model="messageForm.message" 
                          placeholder="Olá {{nome}}! Preparamos algo especial..." 
                          class="min-h-[160px]"
                          :variables="[
                            { label: 'Nome', value: '{{nome}}' }
                          ]"
                        />
                        <p class="text-[11px] text-zinc-500 font-medium">Use <code class="text-primary">&#123;&#123;nome&#125;&#125;</code> para personalizar com o nome do cliente.</p>
                      </div>

                      <div class="grid md:grid-cols-2 gap-6 pt-4">
                        <div class="space-y-3">
                          <Label class="text-xs font-bold text-zinc-400 uppercase">Planos</Label>
                          <TagsInput v-if="filteredPaymentPlans" class="px-0 gap-0 w-full bg-zinc-900/50 border-zinc-700" :model-value="paymentPlanNames">
                            <div class="flex gap-2 flex-wrap items-center px-3">
                              <TagsInputItem v-for="(name, index) in paymentPlanNames" :key="index" :value="name">
                                <TagsInputItemText class="text-[10px]" />
                                <TagsInputItemDelete @click="messageForm.paymentPlanIds?.splice(index, 1); paymentPlanNames.splice(index, 1)" />
                              </TagsInputItem>
                            </div>
                            
                            <ComboboxRoot v-model="paymentPlanNames" v-model:open="isPaymentPlanComboboxOpen" v-model:search-term="searchPaymentPlan" class="w-full">
                              <ComboboxAnchor as-child>
                                <ComboboxInput placeholder="+ Plano..." as-child>
                                  <TagsInputInput @focus="isPaymentPlanComboboxOpen = true" class="w-full px-3 h-7 bg-transparent border-none focus:ring-0 text-xs" :class="(messageForm.paymentPlanIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                                </ComboboxInput>
                              </ComboboxAnchor>
                        
                              <ComboboxPortal>
                                <ComboboxContent class="bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl p-1 z-[9999] min-w-[240px]">
                                  <CommandList
                                    position="popper"
                                    class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                                  >
                                    <CommandEmpty class="p-4 text-xs text-zinc-500">Nenhum plano encontrado</CommandEmpty>
                                    <CommandGroup>
                                      <CommandItem
                                        v-for="plan in filteredPaymentPlans" :key="plan.id" :value="String(plan.name)"
                                        class="text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white cursor-pointer rounded px-3 py-2"
                                        @select.prevent="(ev) => {
                                          if (typeof ev.detail.value === 'string') {
                                            searchPaymentPlan = ''
                                            const p = filteredPaymentPlans?.find(pp => pp.name == ev.detail.value)
                                            messageForm.paymentPlanIds?.push(String(p!.id))
                                            paymentPlanNames.push(p!.name)
                                          }
                        
                                          if (filteredPaymentPlans?.length === 0) {
                                            isPaymentPlanComboboxOpen = false
                                          }
                                        }"
                                      >
                                        {{ plan.name }}
                                      </CommandItem>
                                    </CommandGroup>
                                  </CommandList>
                                </ComboboxContent>
                              </ComboboxPortal>
                            </ComboboxRoot>
                          </TagsInput>
                          <p class="text-[10px] text-gray-400">Você pode selecionar mais de 1 plano</p>
                        </div>

                        <div class="space-y-3">
                          <Label class="text-xs font-bold text-zinc-400 uppercase">Links</Label>
                          <TagsInput v-if="filteredButtons" class="px-0 gap-0 w-full bg-zinc-900/50 border-zinc-700" :model-value="buttonNames">
                            <div class="flex gap-2 flex-wrap items-center px-3">
                              <TagsInputItem v-for="(name, index) in buttonNames" :key="index" :value="name">
                                <TagsInputItemText class="text-[10px]" />
                                <TagsInputItemDelete @click="messageForm.buttonIds?.splice(index, 1); buttonNames.splice(index, 1)" />
                              </TagsInputItem>
                            </div>
                            
                            <ComboboxRoot v-model="buttonNames" v-model:open="isButtonComboboxOpen" v-model:search-term="searchButton" class="w-full">
                              <ComboboxAnchor as-child>
                                <ComboboxInput placeholder="+ Link..." as-child>
                                  <TagsInputInput @focus="isButtonComboboxOpen = true" class="w-full px-3 h-7 bg-transparent border-none focus:ring-0 text-xs" :class="(messageForm.buttonIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                                </ComboboxInput>
                              </ComboboxAnchor>
                        
                              <ComboboxPortal>
                                <ComboboxContent class="bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl p-1 z-[9999] min-w-[240px]">
                                  <CommandList
                                    position="popper"
                                    class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                                  >
                                    <CommandEmpty class="p-4 text-xs text-zinc-500">Nenhum link encontrado</CommandEmpty>
                                    <CommandGroup>
                                      <CommandItem
                                        v-for="btn in filteredButtons" :key="btn.id" :value="String(btn.name)"
                                        class="text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white cursor-pointer rounded px-3 py-2"
                                        @select.prevent="(ev) => {
                                          if (typeof ev.detail.value === 'string') {
                                            searchButton = ''
                                            const b = filteredButtons?.find(bb => bb.name == ev.detail.value)
                                            messageForm.buttonIds?.push(String(b!.id))
                                            buttonNames.push(b!.name)
                                          }
                        
                                          if (filteredButtons?.length === 0) {
                                            isButtonComboboxOpen = false
                                          }
                                        }"
                                      >
                                        {{ btn.name }}
                                      </CommandItem>
                                    </CommandGroup>
                                  </CommandList>
                                </ComboboxContent>
                              </ComboboxPortal>
                            </ComboboxRoot>
                          </TagsInput>
                          <p class="text-[10px] text-gray-400">Você pode selecionar mais de 1 link</p>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>

                <!-- Preview Column -->
                <div class="hidden lg:block relative">
                   <div class="sticky top-0 space-y-4">
                      <div class="flex items-center justify-between px-2">
                         <span class="text-[10px] font-bold text-zinc-500 uppercase tracking-[2px]">Preview Real</span>
                         <Badge variant="outline" class="text-[9px] border-zinc-800 text-zinc-500 capitalize">{{ creationMode }}</Badge>
                      </div>
                      <div class="p-1 rounded-[2.5rem] bg-zinc-900 border border-zinc-800 shadow-2xl overflow-hidden aspect-[9/16] max-h-[560px]">
                         <div class="h-full w-full rounded-[2.2rem] overflow-hidden bg-black/40 relative">
                            <TelegramMessagePreview 
                              :disableInput="true" 
                              :messages="messageFormPreview as MessagePreviewProp[]" 
                              :buttons="messageForm.buttonIds" 
                              :sender="selectedBot?.name" 
                              class="scale-90 origin-top h-[110%] w-[110%] -translate-x-[5%]"
                            />
                         </div>
                      </div>
                   </div>
                </div>
              </div>
            </div>

            <!-- STEP 2: DESTINATÃ RIOS -->
            <div v-if="currentStep === 2" class="max-w-4xl mx-auto space-y-10 animate-in fade-in slide-in-from-right-4 duration-500">
               <div class="text-center space-y-2">
                 <h3 class="text-2xl font-bold text-white">Quem verá sua mensagem?</h3>
                 <p class="text-zinc-400 font-medium tracking-wide">Escolha o público ideal para maximizar o engajamento.</p>
               </div>

               <div class="grid md:grid-cols-2 gap-4">
                  <template v-for="type in MessageRecipientTypeEnum" :key="type">
                    <div 
                      v-if="(!$route.query.flowId && type !== MessageRecipientTypeEnum.FLOW_NODE && type !== MessageRecipientTypeEnum.FLOW_CURRENT_NODE) || (!!$route.query.flowId && (type === MessageRecipientTypeEnum.FLOW_NODE || type === MessageRecipientTypeEnum.FLOW_CURRENT_NODE))"
                      @click="messageForm.recipientType = type"
                      class="relative group cursor-pointer p-5 rounded-2xl border-2 transition-all duration-300 overflow-hidden"
                      :class="[
                        messageForm.recipientType === type 
                        ? 'border-primary bg-primary/5 ring-4 ring-primary/5 shadow-lg shadow-primary/10' 
                        : 'border-white/5 bg-zinc-900/50 hover:border-zinc-700'
                      ]"
                    >
                      <div class="flex items-start gap-4 z-10 relative">
                        <div class="mt-1 p-2 rounded-lg" :class="messageForm.recipientType === type ? 'bg-primary text-black' : 'bg-zinc-800 text-zinc-400'">
                           <Users class="size-5" />
                        </div>
                        <div class="flex-1">
                          <h4 class="font-bold text-white tracking-wide" :class="messageForm.recipientType === type ? 'text-primary' : ''">{{ MessageRecipientTypeLabels[type] }}</h4>
                          <p class="text-xs text-zinc-500 mt-1 leading-relaxed">
                             {{ 
                               type === MessageRecipientTypeEnum.ALL ? 'Envio para 100% da sua base de contatos.' :
                               type === MessageRecipientTypeEnum.SUBSCRIBERS ? 'Focar apenas em assinantes ativos do seu bot.' :
                               type === MessageRecipientTypeEnum.INACTIVE_SUBSCRIBERS ? 'Reative quem já comprou mas não renovou a assinatura.' :
                               type === MessageRecipientTypeEnum.DOWNSELL ? 'Recuperação automática de compras abandonadas.' :
                               type === MessageRecipientTypeEnum.CHAT_JOIN_REQUEST_APPROVAL ? 'Aprove solicitações de entrada e envie uma mensagem de boas-vindas.' :
                               'Público segmentado por critérios específicos.'
                             }}
                          </p>
                        </div>
                        <div v-if="messageForm.recipientType === type" class="animate-in zoom-in duration-300">
                           <Check class="size-6 text-primary" />
                        </div>
                      </div>
                    </div>
                  </template>
               </div>

               <div
                 v-if="messageForm.recipientType && !UNCOUNTABLE_RECIPIENT_TYPES.includes(messageForm.recipientType)"
                 class="flex items-center justify-center gap-2 text-sm font-medium"
               >
                 <template v-if="isLoadingRecipientCount">
                   <Loader2 class="size-4 animate-spin text-zinc-500" />
                   <span class="text-zinc-500">Calculando alcance...</span>
                 </template>
                 <template v-else-if="recipientCount !== null">
                   <Users class="size-4 text-primary" />
                   <span class="text-white">{{ recipientCount }}</span>
                   <span class="text-zinc-400">{{ recipientCount === 1 ? 'lead será alcançado' : 'leads serão alcançados' }}</span>
                 </template>
               </div>

               <!-- Sub-configurations with animations -->
               <div v-if="messageForm.recipientType === MessageRecipientTypeEnum.SUBSCRIBERS || messageForm.recipientType === MessageRecipientTypeEnum.ALL" class="space-y-6 p-8 rounded-3xl bg-zinc-900 border border-zinc-800 animate-in slide-in-from-top-4 duration-500">
                  <div class="flex items-center gap-2 mb-4">
                    <div class="size-2 rounded-full bg-primary animate-pulse" />
                    <h5 class="text-sm font-bold text-white uppercase tracking-wider">Segmentação por Planos (Opcional)</h5>
                  </div>
                  
                  <div class="space-y-4">
                    <Label class="text-xs font-bold text-zinc-400">Filtrar por Planos Adquiridos</Label>
                    <TagsInput v-if="filteredTargetPaymentPlans" class="px-0 gap-0 w-full bg-zinc-950 border-zinc-800" :model-value="targetPaymentPlanNames">
                      <div class="flex gap-2 flex-wrap items-center px-3">
                        <TagsInputItem v-for="(name, index) in targetPaymentPlanNames" :key="index" :value="name" class="bg-primary/10 text-primary border-primary/20">
                           <TagsInputItemText />
                           <TagsInputItemDelete @click="messageForm.targetPaymentPlanIds?.splice(index, 1); targetPaymentPlanNames.splice(index, 1)" />
                        </TagsInputItem>
                      </div>
                      
                      <ComboboxRoot v-model="targetPaymentPlanNames" v-model:open="isTargetPaymentPlanComboboxOpen" v-model:search-term="searchTargetPaymentPlan" class="w-full">
                        <ComboboxAnchor as-child>
                          <ComboboxInput placeholder="+ Adicionar Filtro de Plano..." as-child>
                            <TagsInputInput @focus="isTargetPaymentPlanComboboxOpen = true" class="w-full px-3 h-9 bg-transparent border-none focus:ring-0 text-xs" :class="(messageForm.targetPaymentPlanIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                          </ComboboxInput>
                        </ComboboxAnchor>
                  
                        <ComboboxPortal>
                          <ComboboxContent class="bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl p-1 z-[9999] min-w-[240px]">
                            <CommandList
                              position="popper"
                              class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                            >
                              <CommandEmpty class="p-4 text-xs text-zinc-500 text-center">Planos não encontrados</CommandEmpty>
                              <CommandGroup>
                                <CommandItem
                                  v-for="plan in filteredTargetPaymentPlans" :key="plan.id" :value="String(plan.name)"
                                  class="text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white cursor-pointer rounded px-3 py-2"
                                  @select.prevent="(ev) => {
                                    if (typeof ev.detail.value === 'string') {
                                      searchTargetPaymentPlan = ''
                                      const tp = filteredTargetPaymentPlans?.find(p => p.name == ev.detail.value)
                                      messageForm.targetPaymentPlanIds?.push(String(tp!.id))
                                      targetPaymentPlanNames.push(tp!.name)
                                    }
                  
                                    if (filteredTargetPaymentPlans?.length === 0) {
                                      isTargetPaymentPlanComboboxOpen = false
                                    }
                                  }"
                                >
                                  {{ plan.name }}
                                </CommandItem>
                              </CommandGroup>
                            </CommandList>
                          </ComboboxContent>
                        </ComboboxPortal>
                      </ComboboxRoot>
                    </TagsInput>
                    <p class="text-[10px] text-gray-400">Se nenhum plano for selecionado, o disparo atingirá todos os {{ messageForm.recipientType === MessageRecipientTypeEnum.ALL ? 'Leads' : 'Assinantes' }}.</p>
                  </div>
               </div>
               <div v-if="messageForm.recipientType === MessageRecipientTypeEnum.DOWNSELL" class="space-y-6 p-8 rounded-3xl bg-zinc-900 border border-zinc-800 animate-in slide-in-from-top-4 duration-500">
                  <div class="flex items-center gap-2 mb-4">
                    <div class="size-2 rounded-full bg-primary animate-pulse" />
                    <h5 class="text-sm font-bold text-white uppercase tracking-wider">Configurações de Downsell</h5>
                  </div>
                  
                  <div class="space-y-4">
                    <Label class="text-xs font-bold text-zinc-400">Gatilhos de Abandono (Planos)</Label>
                    <TagsInput v-if="filteredDownsellPaymentPlans" class="px-0 gap-0 w-full bg-zinc-950 border-zinc-800" :model-value="downsellPaymentPlanNames">
                      <div class="flex gap-2 flex-wrap items-center px-3">
                        <TagsInputItem v-for="(name, index) in downsellPaymentPlanNames" :key="index" :value="name" class="bg-red-500/10 text-red-500 border-red-500/20">
                           <TagsInputItemText />
                           <TagsInputItemDelete @click="messageForm.downsellPaymentPlanIds?.splice(index, 1); downsellPaymentPlanNames.splice(index, 1)" />
                        </TagsInputItem>
                      </div>
                      
                      <ComboboxRoot v-model="downsellPaymentPlanNames" v-model:open="isDownsellPaymentPlanComboboxOpen" v-model:search-term="searchDownsellPaymentPlan" class="w-full">
                        <ComboboxAnchor as-child>
                          <ComboboxInput placeholder="+ Adicionar Gatilho..." as-child>
                            <TagsInputInput @focus="isDownsellPaymentPlanComboboxOpen = true" class="w-full px-3 h-9 bg-transparent border-none focus:ring-0 text-xs" :class="(messageForm.downsellPaymentPlanIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                          </ComboboxInput>
                        </ComboboxAnchor>
                  
                        <ComboboxPortal>
                          <ComboboxContent class="bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl p-1 z-[9999] min-w-[240px]">
                            <CommandList
                              position="popper"
                              class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                            >
                              <CommandEmpty class="p-4 text-xs text-zinc-500 text-center">Planos não encontrados</CommandEmpty>
                              <CommandGroup>
                                <CommandItem
                                  v-for="plan in filteredDownsellPaymentPlans" :key="plan.id" :value="String(plan.name)"
                                  class="text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white cursor-pointer rounded px-3 py-2"
                                  @select.prevent="(ev) => {
                                    if (typeof ev.detail.value === 'string') {
                                      searchDownsellPaymentPlan = ''
                                      const dp = filteredDownsellPaymentPlans?.find(p => p.name == ev.detail.value)
                                      messageForm.downsellPaymentPlanIds?.push(String(dp!.id))
                                      downsellPaymentPlanNames.push(dp!.name)
                                    }
                  
                                    if (filteredDownsellPaymentPlans?.length === 0) {
                                      isDownsellPaymentPlanComboboxOpen = false
                                    }
                                  }"
                                >
                                  {{ plan.name }}
                                </CommandItem>
                              </CommandGroup>
                            </CommandList>
                          </ComboboxContent>
                        </ComboboxPortal>
                      </ComboboxRoot>
                    </TagsInput>
                    <p class="text-[10px] text-gray-400">Você pode selecionar mais de 1 plano</p>
                  </div>

                  <div class="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/5">
                     <div class="space-y-1">
                        <Label class="text-white font-bold">Monitorar Chat Abandonado</Label>
                        <p class="text-[11px] text-zinc-500">Dispara se o lead iniciou mas não interagiu pelo tempo determinado.</p>
                     </div>
                     <Switch v-model:checked="messageForm.abandonedChat" />
                  </div>
               </div>

               <!-- Approval Logic -->
               <div v-if="messageForm.recipientType === MessageRecipientTypeEnum.CHAT_JOIN_REQUEST_APPROVAL" class="space-y-6 p-8 rounded-3xl bg-zinc-900 border border-zinc-800 animate-in slide-in-from-top-4 duration-500">
                  <Label class="text-xs font-bold text-zinc-400 uppercase tracking-widest">Selecionar Canais ou Grupos de Monitoramento</Label>
                  <TagsInput v-if="groups" class="px-0 gap-0 w-full bg-zinc-950 border-zinc-800" :model-value="groupNames">
                     <div class="flex gap-2 flex-wrap items-center px-3">
                        <TagsInputItem v-for="(name, index) in groupNames" :key="index" :value="name" class="bg-primary/20 text-primary border-primary/20">
                           <TagsInputItemText />
                           <TagsInputItemDelete @click="messageForm.groupIds?.splice(index, 1); groupNames.splice(index, 1)" />
                        </TagsInputItem>
                     </div>
                     
                     <ComboboxRoot v-model="groupNames" v-model:open="isGroupComboboxOpen" v-model:search-term="searchGroup" class="w-full">
                       <ComboboxAnchor as-child>
                         <ComboboxInput placeholder="+ Adicionar Canal..." as-child>
                           <TagsInputInput @focus="isGroupComboboxOpen = true" class="w-full px-3 h-9 bg-transparent border-none focus:ring-0 text-xs" :class="(messageForm.groupIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                         </ComboboxInput>
                       </ComboboxAnchor>
                 
                       <ComboboxPortal>
                         <ComboboxContent class="bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl p-1 z-[9999] min-w-[240px]">
                           <CommandList
                             position="popper"
                             class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                           >
                             <CommandEmpty class="p-4 text-xs text-zinc-500 text-center">Nenhum canal encontrado</CommandEmpty>
                             <CommandGroup>
                               <CommandItem
                                 v-for="g in filteredGroups" :key="g.id" :value="String(g.name)"
                                 class="text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white cursor-pointer rounded px-3 py-2"
                                 @select.prevent="(ev) => {
                                   if (typeof ev.detail.value === 'string') {
                                     searchGroup = ''
                                     const group = filteredGroups?.find(xxx => xxx.name == ev.detail.value)
                                     if (group && !messageForm.groupIds?.includes(String(group.id))) { messageForm.groupIds?.push(String(group.id))
                                     groupNames.push(group.name) }
                                   }
                 
                                   if (filteredGroups?.length === 0) {
                                     isGroupComboboxOpen = false
                                   }
                                 }"
                               >
                                 {{ g.name }}
                               </CommandItem>
                             </CommandGroup>
                           </CommandList>
                         </ComboboxContent>
                       </ComboboxPortal>
                     </ComboboxRoot>
                  </TagsInput>
                  <p class="text-[10px] text-zinc-600 font-bold">Apenas canais com aprovação automática ativa aparecerão aqui.</p>
               </div>
            </div>

            <!-- STEP 3: AGENDAMENTO -->
            <div v-if="currentStep === 3" class="max-w-xl mx-auto space-y-10 animate-in fade-in slide-in-from-right-4 duration-500">
               <div class="text-center space-y-2">
                 <h3 class="text-2xl font-bold text-white">Quando disparar?</h3>
                 <p class="text-zinc-400 font-medium tracking-wide">Configure o tempo perfeito para sua entrega.</p>
               </div>

               <div v-if="messageForm.recipientType === MessageRecipientTypeEnum.DOWNSELL" class="space-y-6">
                  <div class="p-8 rounded-3xl bg-zinc-900 border border-zinc-800 space-y-6">
                    <div class="flex items-center gap-3">
                       <Hourglass class="size-6 text-primary" />
                       <h4 class="font-bold text-lg text-white">Delay de Recuperação</h4>
                    </div>
                    <p class="text-sm text-zinc-400 leading-relaxed">Defina quanto tempo após o abandono do checkout a mensagem deve ser enviada.</p>
                    <TimePicker v-model="sendingIntervalTime" class="p-4 bg-zinc-950 rounded-2xl border border-zinc-800" />
                  </div>
               </div>

               <div v-else class="space-y-8">
                  <div class="grid grid-cols-2 gap-4">
                    <button 
                      @click="dispatchNow = true"
                      class="p-6 rounded-2xl border-2 transition-all duration-300 flex flex-col items-center gap-2"
                      :class="dispatchNow ? 'border-primary bg-primary/10' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'"
                    >
                      <Rocket class="size-6" :class="dispatchNow ? 'text-primary' : 'text-zinc-500'" />
                      <span class="font-bold text-sm tracking-wide uppercase" :class="dispatchNow ? 'text-white' : 'text-zinc-500'">Imediato</span>
                    </button>
                    <button 
                      @click="dispatchNow = false"
                      class="p-6 rounded-2xl border-2 transition-all duration-300 flex flex-col items-center gap-2"
                      :class="!dispatchNow ? 'border-primary bg-primary/10' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'"
                    >
                      <CalendarIcon class="size-6" :class="!dispatchNow ? 'text-primary' : 'text-zinc-500'" />
                      <span class="font-bold text-sm tracking-wide uppercase" :class="!dispatchNow ? 'text-white' : 'text-zinc-500'">Agendado</span>
                    </button>
                  </div>

                  <div v-if="!dispatchNow" class="space-y-6 animate-in slide-in-from-top-4 duration-500">
                    <div class="p-6 rounded-2xl bg-zinc-900 border border-zinc-800 space-y-4">
                      <Label class="text-xs font-bold text-zinc-400 uppercase tracking-widest">Data & Hora do Início</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" class="w-full h-14 bg-zinc-950 border-zinc-800 text-white font-bold justify-start rounded-xl hover:bg-zinc-800 transition-colors">
                            <CalendarIcon class="mr-3 h-5 w-5 text-primary" />
                            {{ messageForm.sendAt ? messageForm.sendAt.toLocaleString('pt-BR') : 'Clique para selecionar...' }}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent class="w-auto p-0 border-zinc-800 rounded-xl overflow-hidden shadow-2xl">
                          <CalendarTime 
                            mode="single" 
                            initialFocus 
                            locale="pt-BR" 
                            :selected="messageForm.sendAt" 
                            @update:model-value="(date: Date) => {
                              messageForm.sendAt = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes());
                            }"
                            :disabled-dates="{ before: new Date() }" 
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div class="p-6 rounded-2xl bg-zinc-900 border border-zinc-800 space-y-4">
                       <div class="flex items-center gap-2">
                          <Label class="text-xs font-bold text-zinc-400 uppercase tracking-widest">Janela Randômica</Label>
                          <Badge variant="secondary" class="bg-primary/20 text-primary text-[9px] uppercase border-none">Anti-Ban</Badge>
                       </div>
                       <div class="flex items-center gap-4">
                         <div class="flex-1">
                            <Input v-model="messageForm.sendingRange" type="number" placeholder="Ex: 10" class="h-12 bg-zinc-950 border-zinc-800 text-white font-bold" />
                         </div>
                         <span class="text-zinc-500 font-bold">minutos</span>
                       </div>
                       <p class="text-[10px] text-zinc-600 leading-relaxed italic">Proteja sua conta: enviamos cada mensagem em intervalos variados dentro deste tempo para dificultar a detecção de spam pelo Telegram.</p>
                    </div>
                  </div>

                  <div class="p-1 rounded-2xl bg-zinc-800">
                    <div class="p-6 rounded-[calc(1rem-1px)] bg-zinc-950 space-y-6">
                       <div class="flex items-center justify-between">
                          <div class="space-y-1">
                             <Label class="text-zinc-200 font-bold">Envio Único</Label>
                             <p class="text-[11px] text-zinc-600">Desative para transformar em campanha recorrente.</p>
                          </div>
                          <Switch v-model:checked="dispatchOnce" />
                       </div>

                       <div v-if="!dispatchOnce" class="space-y-4 pt-4 border-t border-zinc-800 animate-in slide-in-from-top-2">
                          <Label class="text-xs font-bold text-zinc-400 uppercase">Repetir a cada</Label>
                          <div class="flex items-center gap-3">
                             <Input v-model="messageForm.sendingInterval!" type="number" placeholder="7" class="h-12 w-24 bg-zinc-900 border-zinc-800 text-center font-bold text-primary" />
                             <span class="text-zinc-400 font-bold uppercase tracking-widest text-xs font-bold">Dias Corridos</span>
                          </div>
                       </div>
                    </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Progressive Footer -->
      <DialogFooter class="px-8 py-6 border-t border-zinc-800 bg-zinc-900/80 flex items-center justify-between sm:justify-between w-full">
        <Button 
          variant="ghost" 
          @click="currentStep === 1 ? closeMessageModal() : currentStep--"
          class="h-12 px-6 text-zinc-400 hover:text-white hover:bg-white/5 font-bold transition-all"
        >
          {{ currentStep === 1 ? 'Cancelar Operação' : 'Etapa Anterior' }}
        </Button>

        <div class="flex items-center gap-3">
          <Button 
            v-if="currentStep < 3"
            @click="currentStep++"
            :disabled="!isStepValid"
            class="h-12 px-8 bg-white text-black hover:bg-zinc-200 font-black shadow-[4px_4px_0px_0px_rgba(255,255,255,0.1)] active:translate-y-1 active:shadow-none transition-all group"
          >
            Próximo Passo
            <ArrowRight class="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </Button>
          
          <Button 
            v-else
            @click="save"
            :disabled="isSaving || !isStepValid"
            class="h-12 px-10 bg-primary text-black hover:bg-primary/90 font-black shadow-[4px_4px_0px_0px_rgba(16,185,129,0.3)] active:translate-y-1 active:shadow-none transition-all"
          >
            <Loader2 v-if="isSaving" class="mr-2 h-4 w-4 animate-spin" />
            {{ editingMessage ? 'Salvar Alterações' : 'Lançar Campanha' }}
          </Button>
        </div>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
  <Dialog v-model:open="isShowMessageDialogOpen">
    <DialogScrollContent class="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>Mensagem disparada</DialogTitle>
        <DialogDescription>
          Monitore as informações da sua mensagem.
        </DialogDescription>
      </DialogHeader>
      <div v-if="selectedMessage" class="grid grid-cols-2 gap-6 py-4 text-white">
        <div class="col-span-full text-white grid grid-cols-4 justify-between items-center gap-y-6">
          <div v-if="selectedMessage.sentAt" class="flex justify-center items-center gap-2 flex-col">
            <MessageSquareShare class="size-5 text-primary" />
            <h6 class="font-semibold">Disparado</h6>
            <p class="text-sm">{{ new Date(selectedMessage.sentAt).toLocaleString('pt-BR') }}</p>
          </div>
          <div class="flex justify-center items-center gap-2 flex-col">
            <Send class="size-5 text-primary" />
            <h6 class="font-semibold">Envios</h6>
            <p class="text-sm">{{ selectedMessage.successCount ?? 0 }}</p>
          </div>
          <div class="flex justify-center items-center gap-2 flex-col">
            <X class="size-5 text-red-500" />
            <h6 class="font-semibold">Falhas</h6>
            <p class="text-sm">{{ selectedMessage.failureCount ?? 0 }}</p>
          </div>
          <div class="flex justify-center items-center gap-2 flex-col">
            <Contact class="size-5 text-primary" />
            <h6 class="font-semibold">Destinatários</h6>
            <div class="text-sm">
              {{ MessageRecipientTypeLabels[selectedMessage.recipientType] }}
            </div>
          </div>  
          <div v-if="selectedMessage.sendingInterval?.hours" class="flex justify-center items-center gap-2 flex-col">
            <Hourglass class="size-5 text-primary" />
            <p class="font-semibold">Intervalo de envio</p>
            <p class="text-sm">{{ formatInterval(selectedMessage.sendingInterval) }}</p>
          </div>
          <div v-if="selectedMessage.sendingRange?.minutes" class="flex justify-center items-center gap-2 flex-col">
            <Hourglass class="size-5 text-primary" />
            <p class="font-semibold">Janela de envio</p>
            <p class="text-sm">{{ selectedMessage.sendingRange.minutes }}m</p>
          </div>
        </div>
        <!-- <div class="bg-white/5 rounded-xl text-white p-6 col-span-full">
          <h6 class="font-semibold text-xl pb-2">Descrição</h6>
          <p class="text-sm">{{ selectedMessage.description }}</p>
        </div> -->
        <TelegramMessagePreview class="col-span-full" :messages="previewMessages" :sender="selectedBot?.name" />
      </div>
      <Loader2 v-else class="w-10 h-10 animate-spin text-white mx-auto" />
    </DialogScrollContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { watchDebounced } from '@vueuse/core'
import {
  ArrowsUpDownIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'
import MobileCard from '@/components/ui/mobile-card/MobileCard.vue'
import MobileDataLabel from '@/components/ui/mobile-card/MobileDataLabel.vue'
import {
  Dialog,
  DialogScrollContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import RegisterButton from '@/components/ui/register-button/RegisterButton.vue'
import Table from '@/components/ui/table/Table.vue'
import TableCell from '@/components/ui/table/TableCell.vue'
import TableFallback from '@/components/ui/table/TableFallback.vue'
import TableRow from '@/components/ui/table/TableRow.vue'
import TableBody from '@/components/ui/table/TableBody.vue'
import TableHeader from '@/components/ui/table/TableHeader.vue'
import TableHead from '@/components/ui/table/TableHead.vue'
import TableContainer from '@/components/ui/table/TableContainer.vue'
import { CreateMessageDTO, GetAllMessagesParams, UpdateMessageDTO, useMessage } from '@/composables/useMessage'
import { Message } from '@/interfaces/messages/Message'
import { Pagination as IPagination } from '@/interfaces/Pagination'
import TableRowSkeleton from '@/components/ui/table/TableRowSkeleton.vue'
import { CalendarIcon, CircleHelp, Contact, Eye, GitMerge, Hourglass, Loader2, MessageSquareMore, MessageSquareShare, PencilIcon, Send, X } from 'lucide-vue-next'
import { useToast } from '@/components/ui/toast'
import Pagination from '@/components/ui/pagination/Pagination.vue'
import PageTitle from '@/components/ui/page/PageTitle.vue'
import PageDescription from '@/components/ui/page/PageDescription.vue'
import AnyFileUpload from '@/components/ui/upload/AnyFileUpload.vue'
import { MessageRecipientTypeEnum, MessageRecipientTypeLabels } from '@/enums/messages/MessageRecipientTypeEnum'
import { Switch } from '@/components/ui/switch'
import { CalendarTime } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { MessageStatusEnum } from '@/enums/messages/MessageStatusEnum'
import TimePicker from '@/components/ui/time/TimePicker.vue'
import DeleteConfirmationDialog from '@/components/ui/delete-dialog/DeleteConfirmationDialog.vue'
import { useTime } from '@/composables/useTime'
import { ComboboxAnchor, ComboboxContent, ComboboxInput, ComboboxPortal, ComboboxRoot } from 'radix-vue'
import { PaymentPlan } from '@/interfaces/PaymentPlan'
import { CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { TagsInput, TagsInputInput, TagsInputItem, TagsInputItemDelete, TagsInputItemText } from '@/components/ui/tags-input'
import { usePaymentPlan } from '@/composables/usePaymentPlan'
import { useButton } from '@/composables/useButton'
import { Button as IButton } from '@/interfaces/Button'
import TelegramMessagePreview, { MessagePreviewProp } from '@/components/ui/telegram/TelegramMessagePreview.vue'
import { MessageMediaPlacementEnum } from '@/enums/messages/media/MessageMediaPlacementEnum'
import { useBot } from '@/composables/useBot'
import { PaymentPlanStatusEnum } from '@/enums/payment-plans/PaymentPlanStatusEnum'
import { useGroup } from '@/composables/useGroup'
import { Group } from '@/interfaces/Group'
import { useRoute } from 'vue-router'
import router from '@/router'
import MessageRecipientTypeBadge from '@/components/ui/badge/MessageRecipientTypeBadge.vue'
import { useFlow } from '@/composables/chatFlow/useFlow'
import { NodeTypeEnum, NodeTypeLabels } from '@/enums/chatFlow/nodes/NodeTypeEnum'
import { BotTypeEnum } from '@/enums/bots/BotTypeEnum'
import { Flow } from '@/interfaces/chat-flow/flow/Flow'
import { Info, MessageSquareText, Layout, Users, Rocket, Check, ArrowRight, ArrowLeft, HelpCircle } from 'lucide-vue-next'
import RichTextarea from '@/components/ui/textarea/RichTextarea.vue'
import {
  Stepper,
  StepperItem,
  StepperTrigger,
  StepperSeparator,
  StepperTitle,
  StepperDescription,
  StepperIndicator
} from '@/components/ui/stepper'

const { selectedBot } = useBot()
const { toast } = useToast()
const route = useRoute()
const { create, getAll, getById, update, destroy, countRecipients } = useMessage()

// Stepper Logic
const currentStep = ref(1)
const steps = [
  { step: 1, title: 'Criar', description: 'Conteúdo do disparo', icon: Layout },
  { step: 2, title: 'Público', description: 'Quem deve ver', icon: Users },
  { step: 3, title: 'Enviar', description: 'Data e regras', icon: Rocket },
]

const isStep1Valid = computed(() => {
  const hasName = !!messageForm.value.description
  const hasContent = creationMode.value === 'flow' ? !!messageForm.value.flowId : (!!messageForm.value.message || !!messageForm.value.startMedia || !!startMediaPreview.value)
  return hasName && hasContent
})

const isStep2Valid = computed(() => {
  if (!messageForm.value.recipientType) return false
  if (messageForm.value.recipientType === MessageRecipientTypeEnum.DOWNSELL) {
     return (messageForm.value.downsellPaymentPlanIds?.length ?? 0) > 0 || messageForm.value.abandonedChat
  }
  return true
})

const isStep3Valid = computed(() => {
  if (messageForm.value.recipientType === MessageRecipientTypeEnum.DOWNSELL) return !!sendingIntervalTime.value
  const scheduleValid = dispatchNow.value || !!messageForm.value.sendAt
  const repetitionValid = dispatchOnce.value || !!messageForm.value.sendingInterval
  return scheduleValid && repetitionValid
})

const isStepValid = computed(() => {
  if (currentStep.value === 1) return isStep1Valid.value
  if (currentStep.value === 2) return isStep2Valid.value
  if (currentStep.value === 3) return isStep3Valid.value
  return false
})

const isStepDisabled = (step: number) => {
  if (editingMessage.value) return false // Allow all if editing
  if (step === 1) return false
  if (step === 2) return !isStep1Valid.value
  if (step === 3) return !isStep1Valid.value || !isStep2Valid.value
  return true
}


const { formatInterval } = useTime()
const startMediaPreview = ref()
const startMediaPreviewType = ref()
// Table Configuration
const tableHeaders = [
  { key: 'sendAt', label: 'Envio', sortable: false },
  { key: 'description', label: 'Descrição', sortable: false },
  { key: 'recipient', label: 'Destinatário', sortable: false },
  { key: 'successCount', label: 'Sucesso', sortable: false },
  { key: 'failureCount', label: 'Falha', sortable: false },
  { key: 'status', label: 'Status', sortable: false },
  { key: 'actions', label: 'Ações', class: 'opacity-0', sortable: false },
]
const isSaving = ref(false)
const perPage = ref('10')
const messageFormPreview = computed(() => {
  const messages = []

  if (creationMode.value === 'flow' && messageForm.value.flowId) {
    const selectedFlow = flowsOptions.value.find(f => String(f.id) === String(messageForm.value.flowId))
    if (selectedFlow) {
      messages.push({
        type: 'flow' as const,
        text: selectedFlow.name,
        description: selectedFlow.description || 'Este fluxo iniciará automaticamente para o lead.'
      })
    }
  }

  const startMedia = messageForm.value.startMedia
  const paymentPlansPreview =  paymentPlans.value?.data.filter(i => messageForm.value.paymentPlanIds?.includes(String(i.id)))
  const buttonsPreview =  buttons.value?.data.filter(i => messageForm.value.buttonIds?.includes(String(i.id)))

  if (startMedia) {
    messages.push({
      type: startMedia?.type.split('/')[0] as any,
      source: URL.createObjectURL(startMedia)
    })
  } else if (startMediaPreview.value) {
    messages.push({
      type: startMediaPreviewType.value,
      source: startMediaPreview.value
    })
  }

  if (messageForm.value.message) {
    messages.push({
      type: 'text' as const,
      text: messageForm.value.message
    })
  }

  messages.push(
    ...(paymentPlansPreview ?? []).map(plan => ({
      type: 'button' as const,
      text: plan.buttonMessage
    })),
    ...(buttonsPreview ?? []).map(button => ({
      type: 'button' as const,
      isRedirectLink: true,
      text: button.name
    }))
  )

  return messages
})
const previewMessages = computed(() => {
  const messages: MessagePreviewProp[] = []

  if (selectedMessage.value?.flowId) {
    messages.push({
      type: 'flow',
      text: selectedMessage.value.description // Use description as flow name for existing messages
    })
  }

  const startMedia = selectedMessage.value?.media?.find(media => media.placement === MessageMediaPlacementEnum.START)

  if (startMedia) {
    messages.push({
      type: startMedia?.type as any,
      source: startMedia.source
    })
  }

  messages.push({
      type: 'text',
      text: selectedMessage.value?.message
    },
    ...(selectedMessage.value?.paymentPlans ?? []).map(plan => ({
      type: 'button' as const,
      text: plan.buttonMessage
    })),
    ...(selectedMessage.value?.buttons ?? []).map(button => ({
      type: 'button' as const,
      isRedirectLink: true,
      text: button.name
    }))
  )

  return messages
})

// Filters
const dispatchNow = ref(true)
const dispatchOnce = ref(true)
// Messages data (empty for demo)
const messages = ref<IPagination<Message>>()
// Modal and Form
const isMessageModalOpen = ref(false)
const editingMessage = ref<Message | null>(null)
const messageForm = ref<Partial<CreateMessageDTO>>({
  hasSpoiler: false
})
const deletingMessage = ref<number>()
const isDeleteMessageDialogOpen = ref(false)
const isDeletingMessage = ref(false)
const sendingIntervalTime = ref()
const selectedMessage = ref<Message>()
const isShowMessageDialogOpen = ref(false)
const isLoadingMessage = ref(false)
const isChoiceModalOpen = ref(false)
const creationMode = ref<'standard' | 'flow' | null>(null)
const { intervalToHHMM, convertIntervalToMinutes, convertIntervalToDays } = useTime()

const { getAll: getAllPaymentPlans } = usePaymentPlan()
const paymentPlans = ref<IPagination<PaymentPlan>>()
const filteredPaymentPlans = computed(() => paymentPlans.value?.data.filter(i => !messageForm.value.paymentPlanIds?.includes(String(i.id)) && i.status !== PaymentPlanStatusEnum.INACTIVE))
const searchPaymentPlan = ref('')
const isPaymentPlanComboboxOpen = ref(false)
const paymentPlanNames = ref<string[]>([])

const filteredDownsellPaymentPlans = computed(() => paymentPlans.value?.data.filter(i => !messageForm.value.downsellPaymentPlanIds?.includes(String(i.id)) && i.status !== PaymentPlanStatusEnum.INACTIVE))
const searchDownsellPaymentPlan = ref('')
const isDownsellPaymentPlanComboboxOpen = ref(false)
const downsellPaymentPlanNames = ref<string[]>([])

const filteredTargetPaymentPlans = computed(() => paymentPlans.value?.data.filter(i => !messageForm.value.targetPaymentPlanIds?.includes(String(i.id)) && i.status !== PaymentPlanStatusEnum.INACTIVE))
const searchTargetPaymentPlan = ref('')
const isTargetPaymentPlanComboboxOpen = ref(false)
const targetPaymentPlanNames = ref<string[]>([])

const UNCOUNTABLE_RECIPIENT_TYPES = [MessageRecipientTypeEnum.DOWNSELL, MessageRecipientTypeEnum.CHAT_JOIN_REQUEST_APPROVAL]
const recipientCount = ref<number | null>(null)
const isLoadingRecipientCount = ref(false)

watchDebounced(
  () => [
    messageForm.value.recipientType,
    messageForm.value.targetPaymentPlanIds?.join(','),
    messageForm.value.flowId,
    messageForm.value.nodeId
  ],
  async () => {
    const recipientType = messageForm.value.recipientType

    if (!recipientType || UNCOUNTABLE_RECIPIENT_TYPES.includes(recipientType)) {
      recipientCount.value = null
      return
    }

    if ((recipientType === MessageRecipientTypeEnum.FLOW_NODE || recipientType === MessageRecipientTypeEnum.FLOW_CURRENT_NODE) && (!messageForm.value.flowId || !messageForm.value.nodeId)) {
      recipientCount.value = null
      return
    }

    isLoadingRecipientCount.value = true
    try {
      recipientCount.value = await countRecipients({
        recipientType,
        targetPaymentPlanIds: messageForm.value.targetPaymentPlanIds,
        flowId: messageForm.value.flowId,
        nodeId: messageForm.value.nodeId
      })
    } catch {
      recipientCount.value = null
    } finally {
      isLoadingRecipientCount.value = false
    }
  },
  { debounce: 400, immediate: true }
)

const { getAll: getAllButtons } = useButton()
const buttons = ref<IPagination<IButton>>()
const filteredButtons = computed(() => buttons.value?.data.filter(i => !messageForm.value.buttonIds?.includes(String(i.id))))
const searchButton = ref('')
const isButtonComboboxOpen = ref(false)
const buttonNames = ref<string[]>([])

const { getAll: getAllGroups } = useGroup()
const groups = ref<IPagination<Group>>()
const filteredGroups = computed(() => groups.value?.data.filter(i => !messageForm.value.groupIds?.includes(String(i.id))))
const searchGroup = ref('')
const isGroupComboboxOpen = ref(false)
const groupNames = ref<string[]>([])

const { getById: getFlowById, getAll: getAllFlows } = useFlow()
const flowsOptions = ref<Flow[]>([])
const flow = ref()
const node = computed(() => {
  if (flow.value && route.query.nodeId) {
    return flow.value.definition.nodes.find((node: any) => node.id === route.query.nodeId)
  }
  return null
})

// Actions
const openNewMessageModal = () => {
  if (selectedBot.value?.type === BotTypeEnum.CHAT_FLOW) {
    isChoiceModalOpen.value = true
    return
  }

  selectMessageType('standard')
}

const selectMessageType = (type: 'standard' | 'flow') => {
  creationMode.value = type
  isChoiceModalOpen.value = false
  
  dispatchNow.value = true
  sendingIntervalTime.value = undefined
  startMediaPreview.value = undefined
  isPaymentPlanComboboxOpen.value = false
  isButtonComboboxOpen.value = false
  isGroupComboboxOpen.value = false
  isDownsellPaymentPlanComboboxOpen.value = false
  searchPaymentPlan.value = ''
  searchDownsellPaymentPlan.value = ''
  searchButton.value = ''
  searchGroup.value = ''
  paymentPlanNames.value = []
  downsellPaymentPlanNames.value = []
  targetPaymentPlanNames.value = []
  buttonNames.value = []
  groupNames.value = []
  editingMessage.value = null
  messageForm.value = { paymentPlanIds: [], buttonIds: [], groupIds: [], downsellPaymentPlanIds: [], targetPaymentPlanIds: [], hasSpoiler: false }
  isMessageModalOpen.value = true
  currentStep.value = 1
}

const closeMessageModal = () => {
  isMessageModalOpen.value = false
  currentStep.value = 1
}

const showMessage = async (id: number) => {
  selectedMessage.value = undefined
  isShowMessageDialogOpen.value = true
  selectedMessage.value = await getById(id)
}
const editMessage = async (message: Message) => {
  // editingMessage.value = message.id
  // const delayInSeconds = message.newMembersAcceptanceDelay ? convertIntervalToSeconds(message.newMembersAcceptanceDelay) : 0
  // const { value: newMembersAcceptanceDelay, unit } = convertFromSeconds(delayInSeconds);

  // timeUnit.value = unit;
  // messageForm.value = {
  //   name: message.name,
  //   messageId: message.messageId,
  //   category: message.category,
  //   newMembersAcceptanceDelay,
  // }
  startMediaPreview.value = undefined
  isMessageModalOpen.value = true
  currentStep.value = 1
  isLoadingMessage.value = true
  editingMessage.value = await getById(message.id)
  isLoadingMessage.value = false

  creationMode.value = editingMessage.value.flowId ? 'flow' : 'standard'

  messageForm.value = {
    description: editingMessage.value.description,
    message: editingMessage.value.message,
    paymentPlanIds: editingMessage.value.paymentPlans?.map(p => p.id.toString()),
    buttonIds: editingMessage.value.buttons?.map(b => b.id.toString()),
    groupIds: editingMessage.value.groups?.map(g => g.id.toString()),
    downsellPaymentPlanIds: editingMessage.value.downsellPaymentPlans?.map(p => p.id.toString()),
    targetPaymentPlanIds: editingMessage.value.targetPaymentPlans?.map(p => p.id.toString()),
    recipientType: editingMessage.value.recipientType,
    sendingRange: editingMessage.value.sendingRange ? convertIntervalToMinutes(editingMessage.value.sendingRange) : undefined,
    sendAt: editingMessage.value.sendAt ? new Date(editingMessage.value.sendAt) : undefined,
    sendingInterval: editingMessage.value.sendingInterval && editingMessage.value.recipientType !== MessageRecipientTypeEnum.DOWNSELL ? convertIntervalToDays(editingMessage.value.sendingInterval) : editingMessage.value.sendingInterval as number,
    abandonedChat: editingMessage.value.abandonedChat,
    flowId: editingMessage.value.flowId ? String(editingMessage.value.flowId) : undefined,
    hasSpoiler: editingMessage.value.media?.find(m => m.placement === MessageMediaPlacementEnum.START)?.hasSpoiler ?? false,
  }
  if (messageForm.value.sendingInterval) {
    dispatchOnce.value = false
    dispatchNow.value = false
  } else {
    dispatchOnce.value = true
    dispatchNow.value = true
  }
  const startMedia = editingMessage.value.media?.find(m => m.placement === MessageMediaPlacementEnum.START)
  if (startMedia) {
    startMediaPreview.value = startMedia.source
    startMediaPreviewType.value = startMedia.type
  }
  sendingIntervalTime.value = editingMessage.value.sendingInterval ? intervalToHHMM(editingMessage.value.sendingInterval) : undefined
  paymentPlanNames.value = editingMessage.value.paymentPlans?.map(p => p.name) ?? []
  buttonNames.value = editingMessage.value.buttons?.map(b => b.name) ?? []
  groupNames.value = editingMessage.value.groups?.map(g => g.name) ?? []
  downsellPaymentPlanNames.value = editingMessage.value.downsellPaymentPlans?.map(p => p.name) ?? []
  targetPaymentPlanNames.value = editingMessage.value.targetPaymentPlans?.map(p => p.name) ?? []
}

const deleteMessage = (message: any) => {
  isDeleteMessageDialogOpen.value = true
  deletingMessage.value = message.id
}

const sortBy = (key: string) => {
  // Implement sorting logic
}

const save = async () => {
  isSaving.value = true

  try {
    let payload = { ...messageForm.value }

    if (payload.flowId) {
      payload.flowId = Number(payload.flowId)
    }

    if (dispatchNow.value || payload.recipientType === MessageRecipientTypeEnum.DOWNSELL) {
      delete payload.sendAt
      delete payload.sendingRange
    }

    if (dispatchOnce.value || !payload.sendingInterval) {
      payload.sendingInterval = null
    }

    if (payload.sendingRange) {
      payload.sendingRange = payload.sendingRange * 60
    }

    if (payload.sendingInterval) {
      payload.sendingInterval = payload.sendingInterval * 24 * 60 * 60
    }

    if (payload.recipientType === MessageRecipientTypeEnum.DOWNSELL) {
      const [hours, minutes] = sendingIntervalTime.value.split(':').map(Number)
      payload.sendingInterval = (hours * 60 * 60) + (minutes * 60)
    }

    payload.abandonedChat = !!payload.abandonedChat

    const { 
      paymentPlanIds, 
      buttonIds, 
      groupIds, 
      downsellPaymentPlanIds, 
      targetPaymentPlanIds,
      ...cleanPayload 
    } = payload

    if (!editingMessage.value) {
      const message = await create(cleanPayload as CreateMessageDTO, paymentPlanIds as any, buttonIds as any, groupIds as any, downsellPaymentPlanIds as any, targetPaymentPlanIds as any)
      toast({
        title: 'Mensagem criada com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
      messages.value?.data.push({ ...message, successCount: 0, failureCount: 0 })
    } else {
      delete (cleanPayload as any).recipientType
      const message = await update(editingMessage.value.id, cleanPayload as UpdateMessageDTO, paymentPlanIds as any, buttonIds as any, groupIds as any, downsellPaymentPlanIds as any, targetPaymentPlanIds as any)
      toast({
        title: 'Mensagem editada com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
      const index = messages.value?.data.findIndex(message => message.id == editingMessage.value!.id)
      if (index !== -1) {
        messages.value!.data[index!] = { ...message, ...editingMessage.value, ...messageForm.value } as unknown as Message
      }
    }

    isMessageModalOpen.value = false
  } finally {
    isSaving.value = false
  }
}

function reloadAndGetAll(params: GetAllMessagesParams) {
  messages.value = undefined
  getAll(params)
    .then(data => messages.value = data)
}

watch(() => perPage.value, () => {
  reloadAndGetAll({ perPage: perPage.value })
})

watch(() => isMessageModalOpen.value, () => {
  if (!paymentPlans.value) {
    getAllPaymentPlans({ perPage: '50' })
      .then((data) => {
        paymentPlans.value = data
        if (route.query.recipientType === MessageRecipientTypeEnum.DOWNSELL && route.query.downsellPaymentPlanIds) {
          messageForm.value.recipientType = MessageRecipientTypeEnum.DOWNSELL
          const paymentPlan = paymentPlans.value?.data.find(plan => plan.id == (route.query.downsellPaymentPlanIds as unknown as number))
          downsellPaymentPlanNames.value = [paymentPlan?.name ?? '']
          messageForm.value.downsellPaymentPlanIds = [route.query.downsellPaymentPlanIds]
        }
      })
  }
  if (!buttons.value) {
    getAllButtons()
      .then((data) => buttons.value = data)
  }

  if (selectedBot.value?.type === BotTypeEnum.CHAT_FLOW && flowsOptions.value.length === 0) {
    getAllFlows({ perPage: '100' }).then(data => flowsOptions.value = data.data)
  }

  if (!isMessageModalOpen.value) {
    flow.value = undefined
    router.replace({
      query: {}
    })
  }
})

watch(() => messageForm.value.recipientType, async () => {
  if (messageForm.value.recipientType === MessageRecipientTypeEnum.CHAT_JOIN_REQUEST_APPROVAL && !groups.value) {
    groups.value = await getAllGroups({ perPage: '50' })
  }
})

onMounted(() => {
  getAll()
    .then((data) => messages.value = data)

  if ((route.query.recipientType === MessageRecipientTypeEnum.DOWNSELL && route.query.downsellPaymentPlanIds) || (route.query.flowId && route.query.nodeId)) {
    openNewMessageModal()
    if (route.query.flowId && route.query.nodeId) {
      getFlowById(Number(route.query.flowId))
        .then((data) => {
          flow.value = data
          messageForm.value.recipientType = MessageRecipientTypeEnum.FLOW_NODE
          messageForm.value.flowId = Number(route.query.flowId)
          messageForm.value.nodeId = route.query.nodeId as string
        })
    }
  }
})
</script>
